import React from 'react';
import { UploadProgressBarProps } from '../types';

const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
  fileName,
  progress,
  status,
  error,
  onCancel,
  onRetry,
  onRemove
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'uploading':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return (
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'completed':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Helper function for file size formatting - currently unused but kept for future use
  // const formatFileSize = (bytes: number) => {
  //   if (bytes === 0) return '0 Bytes';
  //   const k = 1024;
  //   const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  //   const i = Math.floor(Math.log(bytes) / Math.log(k));
  //   return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  // };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm" style={{ minHeight: '60px' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3">
          <div className={`p-1 rounded-full text-white ${getStatusColor()}`}>
            {getStatusIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {fileName}
            </p>
            {error && (
              <p className="text-xs text-red-600 mt-1">
                {error}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {status === 'uploading' && onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 p-1"
              title="Cancel upload"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          
          {status === 'failed' && onRetry && (
            <button
              onClick={onRetry}
              className="text-blue-500 hover:text-blue-700 p-1"
              title="Retry upload"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          
          {(status === 'completed' || status === 'failed') && onRemove && (
            <button
              onClick={onRemove}
              className="text-gray-400 hover:text-gray-600 p-1"
              title="Remove from list"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${getStatusColor()}`}
          style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
        ></div>
      </div>
      
      {/* Progress text */}
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-gray-500">
          {status === 'uploading' && `${Math.round(progress)}%`}
          {status === 'completed' && 'Upload complete'}
          {status === 'failed' && 'Upload failed'}
        </span>
        {status === 'uploading' && (
          <span className="text-xs text-gray-500">
            Uploading...
          </span>
        )}
      </div>
    </div>
  );
};

export default React.memo(UploadProgressBar); 